import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  Search,
  Save,
  AlertCircle,
  ShieldCheck,
  FileText,
  Download,
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useKPIStore } from "../../store/kpiStore";
import { usePremisesStore } from "../../store/store";

type ItemStatus =
  | "Complied"
  | "Partially Complied"
  | "Not Complied"
  | "Not Applicable"
  | ""
  | null
  | undefined;

interface AssessmentItem {
  srNo: number;
  section: string;
  question: string;
  status: ItemStatus;
  remarks: string;
}

const STATUS_OPTIONS: ItemStatus[] = [
  "Complied",
  "Partially Complied",
  "Not Complied",
  "Not Applicable",
];

const statusPill = (status?: ItemStatus) => {
  switch (status) {
    case "Complied":
      return "bg-green-100 text-green-800 border-green-200";
    case "Partially Complied":
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case "Not Complied":
      return "bg-red-100 text-red-800 border-red-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

const statusIcon = (status?: ItemStatus) => {
  switch (status) {
    case "Complied":
      return <CheckCircle className="w-4 h-4" />;
    case "Partially Complied":
      return <AlertTriangle className="w-4 h-4" />;
    case "Not Complied":
      return <XCircle className="w-4 h-4" />;
    default:
      return <Clock className="w-4 h-4" />;
  }
};

const isUnanswered = (status: ItemStatus): boolean => {
  return !status || status === "Not Applicable" || (status as any) === "";
};

const computeStats = (items: AssessmentItem[]) => {
  const total = items.length;
  const complied = items.filter((i) => i.status === "Complied").length;
  const partially = items.filter(
    (i) => i.status === "Partially Complied"
  ).length;
  const notCmp = items.filter((i) => i.status === "Not Complied").length;
  const pending = items.filter((i) => isUnanswered(i.status)).length;

  const toPct = (count: number) => (total ? Math.round((count / total) * 100) : 0);

  return {
    total,
    complied,
    partially,
    notCmp,
    pending,
    compliedPct: toPct(complied),
    partiallyPct: toPct(partially),
    notCmpPct: toPct(notCmp),
    pendingPct: toPct(pending),
  };
};

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const AssessmentDetail: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const query = useQuery();
  const assessmentId = params.id as string;

  const mode = (query.get("mode") || "view").toLowerCase();
  const isEditMode = mode === "edit";

  const kpi = useKPIStore();
  const {
    fetchAssessment,
    fetchAssessmentItems,
    updateAssessmentItem,
    submitAssessment,
    assessment,
    assessmentItems,
    loading,
  } = kpi || ({} as any);

  const { premiseMap } = usePremisesStore();

  const [searchTerm, setSearchTerm] = useState("");
  const [sectionCollapsed, setSectionCollapsed] = useState<Set<string>>(
    new Set()
  );
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string>("");
  const [showAllAttachments, setShowAllAttachments] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);

  const debounceTimers = useRef<Record<number, any>>({});

  const load = useCallback(async () => {
    if (!assessmentId) return;
    await fetchAssessment(assessmentId);
    await fetchAssessmentItems(assessmentId);
  }, [assessmentId, fetchAssessment, fetchAssessmentItems]);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const bySection: Record<string, AssessmentItem[]> = {};
    (assessmentItems || []).forEach((it: any) => {
      const key = it.section || "General";
      if (!bySection[key]) bySection[key] = [];
      bySection[key].push({
        srNo: it.srNo,
        section: it.section || "General",
        question: it.question,
        status: (it.status || "") as ItemStatus,
        remarks: it.remarks || "",
      });
    });
    // sort by order of srNo inside section
    Object.keys(bySection).forEach((s) =>
      bySection[s].sort((a, b) => a.srNo - b.srNo)
    );
    // const sections = Object.keys(bySection).sort((a, b) => a.localeCompare(b));
    const sections = Object.keys(bySection);
    return sections.map((name) => ({ name, items: bySection[name] }));
  }, [assessmentItems]);

  const filteredGrouped = useMemo(() => {
    if (!searchTerm.trim()) return grouped;
    const term = searchTerm.trim().toLowerCase();
    return grouped
      .map((sec) => ({
        ...sec,
        items: sec.items.filter(
          (it) =>
            it.question.toLowerCase().includes(term) ||
            (it.remarks && it.remarks.toLowerCase().includes(term)) ||
            String(it.srNo).includes(term)
        ),
      }))
      .filter((sec) => sec.items.length > 0);
  }, [grouped, searchTerm]);

  const overallStats = useMemo(() => {
    const allItems = (grouped || []).flatMap((g) => g.items);
    return computeStats(allItems);
  }, [grouped]);

  const distributionSegments = useMemo(() => {
    const total = overallStats.total || 0;
    const percent = (count: number) => (total ? (count / total) * 100 : 0);

    return [
      {
        key: "complied",
        label: "Complied",
        count: overallStats.complied,
        percent: percent(overallStats.complied),
        color: "#10b981",
        legendClass: "bg-green-500",
        textClass: "text-green-500",
      },
      {
        key: "partially",
        label: "Partially Complied",
        count: overallStats.partially,
        percent: percent(overallStats.partially),
        color: "#f59e0b",
        legendClass: "bg-yellow-500",
        textClass: "text-yellow-500",
      },
      {
        key: "notComplied",
        label: "Not Complied",
        count: overallStats.notCmp,
        percent: percent(overallStats.notCmp),
        color: "#ef4444",
        legendClass: "bg-red-500",
        textClass: "text-red-500",
      },
      {
        key: "pending",
        label: "Pending / N/A",
        count: overallStats.pending,
        percent: percent(overallStats.pending),
        color: "#9ca3af",
        legendClass: "bg-gray-400",
        textClass: "text-gray-500",
      },
    ];
  }, [overallStats]);

  const circumference = useMemo(() => 2 * Math.PI * 40, []);

  const handleExpandAll = () => setSectionCollapsed(new Set());
  const handleCollapseAll = () =>
    setSectionCollapsed(new Set(grouped.map((g) => g.name)));
  const toggleSection = (name: string) =>
    setSectionCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const findNextUnansweredQuestion = (currentSrNo: number): number | null => {
    const allItems = grouped.flatMap(g => g.items);
    const currentIndex = allItems.findIndex(item => item.srNo === currentSrNo);
    
    for (let i = currentIndex + 1; i < allItems.length; i++) {
      const item = allItems[i];
      if (isUnanswered(item.status)) {
        return item.srNo;
      }
    }
    
    for (let i = 0; i < currentIndex; i++) {
      const item = allItems[i];
      if (isUnanswered(item.status)) {
        return item.srNo;
      }
    }
    
    return null; 
  };

  const scrollToQuestion = (srNo: number) => {
    const element = document.getElementById(`question-${srNo}`);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
      

      element.classList.add('ring-2', 'ring-blue-300', 'ring-opacity-50');
      setTimeout(() => {
        element.classList.remove('ring-2', 'ring-blue-300', 'ring-opacity-50');
      }, 2000);
    }
  };

  const onStatusChange = async (srNo: number, status: ItemStatus) => {
    try {
      setSaveError("");
      await updateAssessmentItem(assessmentId, srNo, { status: status as any });
      setLastSaved(new Date());
      
      if (status !== "Not Applicable" && status !== "") {
        const nextSrNo = findNextUnansweredQuestion(srNo);
        if (nextSrNo !== null) {
          const allItems = grouped.flatMap(g => g.items);
          const nextItem = allItems.find(item => item.srNo === nextSrNo);
          if (nextItem) {
            setSectionCollapsed(prev => {
              const next = new Set(prev);
              next.delete(nextItem.section);
              return next;
            });
            
            setTimeout(() => scrollToQuestion(nextSrNo), 300);
          }
        } else {
          setTimeout(() => {
            const completionElement = document.getElementById('completion-message');
            if (completionElement) {
              completionElement.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center' 
              });
            }
          }, 300);
        }
      }
    } catch (e: any) {
      setSaveError(e?.message || "Failed to update status");
    }
  };

  const onRemarksChange = (srNo: number, value: string) => {
    if (debounceTimers.current[srNo])
      clearTimeout(debounceTimers.current[srNo]);
    debounceTimers.current[srNo] = setTimeout(async () => {
      try {
        setSaveError("");
        await updateAssessmentItem(assessmentId, srNo, {
          remarks: value,
          //   status: "" as any,
        });
        setLastSaved(new Date());
      } catch (e: any) {
        setSaveError("Failed to update remarks");
      }
    }, 400);
  };

  const onSubmitAssessment = async () => {
    try {
      await submitAssessment(assessmentId);
      await load();
    } catch (e: any) {
      setSaveError("Failed to submit assessment");
    }
  };

  const handleDownloadPDF = () => {
    try {
      const doc = new jsPDF({ orientation: "landscape" });
      const premiseName = premiseMap[assessment?.premiseId as string] || "N/A";

      doc.setFontSize(18);
      doc.setTextColor(0, 168, 165);
      doc.text("KPI Assessment Report", 14, 18);
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(`Assessment: ${assessment?.name || "N/A"}`, 14, 26);
      doc.text(`Premise: ${premiseName}`, 14, 32);
      doc.text(`Date: ${assessment?.date || "N/A"}`, 14, 38);
      doc.text(`Status: ${String(assessment?.status || "N/A").toUpperCase()}`, 14, 44);
      doc.text(
        `Complied: ${overallStats.compliedPct}%  |  Partially: ${overallStats.partiallyPct}%  |  Not Complied: ${overallStats.notCmpPct}%  |  Pending/N/A: ${overallStats.pendingPct}%`,
        14, 50
      );

      let startY = 58;

      grouped.forEach((sec) => {
        // Section header bar
        doc.setFontSize(10);
        doc.setTextColor(255, 255, 255);
        doc.setFillColor(30, 64, 175);
        const pageW = doc.internal.pageSize.width;
        doc.rect(14, startY - 5, pageW - 28, 8, "F");
        doc.text(sec.name, 16, startY);
        startY += 4;

        autoTable(doc, {
          startY,
          head: [["Sr.No", "Question", "Status", "Remarks"]],
          body: sec.items.map((item) => [
            item.srNo,
            item.question,
            item.status || "Pending",
            item.remarks || "—",
          ]),
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [0, 168, 165] },
          columnStyles: {
            0: { cellWidth: 12 },
            1: { cellWidth: 120 },
            2: { cellWidth: 35 },
            3: { cellWidth: "auto" as any },
          },
          didDrawPage: () => { startY = 20; },
        });

        startY = (doc as any).lastAutoTable.finalY + 8;
      });

      doc.save(`KPI_Assessment_${(assessment?.name || "Report").replace(/[^a-zA-Z0-9]/g, "_")}.pdf`);
    } catch (err) {
      console.error("PDF error:", err);
      alert("Failed to generate PDF.");
    }
  };

  const headerStatusPill = useMemo(() => {
    const st = assessment?.status || "Draft";
    const cls =
      st === "submitted"
        ? "bg-green-100 text-green-800"
        : st === "draft"
        ? "bg-gray-100 text-gray-800"
        : "bg-gray-100 text-gray-800";
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${cls}`}>
        {String(st).toUpperCase()}
      </span>
    );
  }, [assessment]);

  return (
    <div className="w-full px-6 py-4">
      
      <div className="w-full mt-20 mb-6">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => navigate("/safety-equipment/kpi-checklist")}
            className="inline-flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="hidden sm:flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search questions or remarks…"
                className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
              />
            </div>

            <button
              onClick={handleExpandAll}
              className="px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors flex items-center gap-1"
            >
              <Eye className="w-4 h-4" />
              Expand All
            </button>
            <button
              onClick={handleCollapseAll}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors flex items-center gap-1"
            >
              <EyeOff className="w-4 h-4" />
              Collapse All
            </button>

            <button
              onClick={() => setShowDashboard(!showDashboard)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                showDashboard 
                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <AlertCircle className="w-4 h-4" />
              {showDashboard ? 'Hide Dashboard' : 'Show Dashboard'}
            </button>

            {isEditMode && assessment?.status !== "submitted" && (
              <button
                onClick={onSubmitAssessment}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors flex items-center gap-2"
                disabled={loading}
                title="Submit assessment and compute compliance"
              >
                <ShieldCheck className="w-4 h-4" />
                Submit
              </button>
            )}
            <button
              onClick={handleDownloadPDF}
              className="px-4 py-2 bg-[#00A8A5] text-white rounded-lg font-medium hover:bg-[#008A87] transition-colors flex items-center gap-2"
              title="Download PDF report"
            >
              <Download className="w-4 h-4" />
              Download PDF
            </button>
          </div>
        </div>

        <div className="bg-white border rounded-lg shadow-sm">
          <div className="px-5 py-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900 truncate">
                  {assessment?.name || "Assessment"}
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                  <span className="inline-flex items-center gap-1">
                    <span className="text-gray-500">Date:</span>
                    <span className="font-medium text-gray-800">
                      {assessment?.date || "-"}
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="text-gray-500">Premise:</span>
                    <span className="font-medium text-gray-800">
                      {premiseMap[assessment?.premiseId as string] || "N/A"}
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="text-gray-500">Status:</span>
                    <span>{headerStatusPill}</span>
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="text-gray-500">Total items:</span>
                    <span className="font-medium text-gray-800">
                      {Number.isFinite(assessment?.totalItems)
                        ? assessment?.totalItems
                        : 0}
                    </span>
                  </span>
                </div>
              <div className="mt-2">
                <div className="text-xs text-gray-500 mb-1">
                  Attachments {assessment?.attachments?.length && assessment.attachments.length > 0 && (
                    <span className="text-gray-400">({assessment.attachments.length})</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {(assessment?.attachments || [])
                    .slice(0, showAllAttachments ? assessment?.attachments?.length || 30 : 3)
                    .map((att: any) => (
                      <a
                        key={att.id}
                        href={att.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 px-2 py-1 rounded border text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                        title={att.name}
                      >
                        <FileText className="w-3.5 h-3.5 text-gray-500" />
                        <span className="truncate max-w-[120px]">{att.name}</span>
                      </a>
                    ))}
                  
                  {assessment?.attachments && assessment.attachments.length > 3 && (
                    <button
                      onClick={() => setShowAllAttachments(!showAllAttachments)}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs font-medium transition-colors"
                    >
                      {showAllAttachments ? (
                        <>
                          <span>Show Less</span>
                          <ChevronUp className="w-3 h-3" />
                        </>
                      ) : (
                        <>
                          <span>+{assessment.attachments.length - 3} more</span>
                          <ChevronDown className="w-3 h-3" />
                        </>
                      )}
                    </button>
                  )}
                  
                  {(!assessment?.attachments || assessment.attachments.length === 0) && (
                    <span className="text-xs text-gray-400">No files attached</span>
                  )}
                </div>
              </div>
              </div>

              <div className="flex sm:hidden flex-col gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search questions or remarks…"
                    className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleExpandAll}
                    className="flex-1 px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors"
                  >
                    Expand
                  </button>
                  <button
                    onClick={handleCollapseAll}
                    className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                  >
                    Collapse
                  </button>
                  <button
                    onClick={() => setShowDashboard(!showDashboard)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      showDashboard 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {showDashboard ? 'Hide' : 'Dashboard'}
                  </button>
                  {isEditMode && assessment?.status !== "submitted" && (
                    <button
                      onClick={onSubmitAssessment}
                      className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
                      disabled={loading}
                    >
                      Submit
                    </button>
                  )}
                </div>
              </div>

              {isEditMode && assessment?.status !== "submitted" && <div className="flex items-center gap-2">
                <input
                  type="file"
                  id="assessment-file"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      await kpi.uploadAssessmentAttachment(assessmentId, file);
                      // await kpi.fetchAssessment(assessmentId);
                    } catch (err) {
                      console.error("Failed to upload file", err);
                    }
                  }}
                  accept=".pdf,.doc,.docx,.xlsx,.xls,image/*"
                />
                <label
                  htmlFor="assessment-file"
                  className="px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors cursor-pointer"
                >
                  Upload Attachment
                </label>
              </div>}
            </div>
          </div>
        </div>
      </div>


      {showDashboard && (
        <div className="bg-white border rounded-lg shadow-sm mb-6">
          <div className="px-5 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Assessment Dashboard</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-green-600 mb-1">
                  {overallStats.compliedPct}%
                </div>
                <div className="text-sm font-medium text-green-700 mb-1">Complied</div>
                <div className="text-xs text-green-600">
                  {overallStats.complied} of {overallStats.total} items
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-yellow-600 mb-1">
                  {overallStats.partiallyPct}%
                </div>
                <div className="text-sm font-medium text-yellow-700 mb-1">Partially Complied</div>
                <div className="text-xs text-yellow-600">
                  {overallStats.partially} of {overallStats.total} items
                </div>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-red-600 mb-1">
                  {overallStats.notCmpPct}%
                </div>
                <div className="text-sm font-medium text-red-700 mb-1">Not Complied</div>
                <div className="text-xs text-red-600">
                  {overallStats.notCmp} of {overallStats.total} items
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-gray-600 mb-1">
                  {overallStats.pendingPct}%
                </div>
                <div className="text-sm font-medium text-gray-700 mb-1">Pending / N&A</div>
                <div className="text-xs text-gray-600">
                  {overallStats.pending} of {overallStats.total} items
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-800 mb-4 text-center">Compliance Distribution</h4>
                <div className="flex items-center justify-center">
                  <div className="relative w-48 h-48">
                    <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="#e5e7eb"
                        strokeWidth="8"
                      />
                      {(() => {
                        let cumulativeOffset = 0;
                        return distributionSegments.map((segment) => {
                          if (segment.percent <= 0) return null;
                          const segmentLength = (segment.percent / 100) * circumference;
                          const circleElement = (
                            <circle
                              key={segment.key}
                              cx="50"
                              cy="50"
                              r="40"
                              fill="none"
                              stroke={segment.color}
                              strokeWidth="8"
                              strokeDasharray={`${segmentLength} ${circumference}`}
                              strokeDashoffset={`-${cumulativeOffset}`}
                              strokeLinecap="round"
                              className="transition-all duration-1000"
                            />
                          );
                          cumulativeOffset += segmentLength;
                          return circleElement;
                        });
                      })()}
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-800">{overallStats.total}</div>
                        <div className="text-xs text-gray-600">Total Items</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap justify-center mt-4 gap-4">
                  {distributionSegments.map((segment) => (
                    <div className="flex items-center" key={`${segment.key}-legend`}>
                      <div className={`w-3 h-3 rounded-full mr-2 ${segment.legendClass}`}></div>
                      <span className="text-xs text-gray-600">
                        {segment.label} ({segment.count})
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <h4 className="text-md font-semibold text-gray-800 mb-2 text-center">Section Breakdown</h4>
                <div className="space-y-3">
                  {filteredGrouped.slice(0, 8).map((sec) => {
                    const sectionStats = computeStats(sec.items);
                    return (
                      <div key={sec.name} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700 truncate">{sec.name}</span>
                          <span className="text-gray-600 font-medium">{sectionStats.total} items</span>
                        </div>
                         <div className="w-full bg-gray-200 rounded-full h-2">
                           <div className="flex h-2 rounded-full overflow-hidden">
                             <div 
                               className="bg-green-500" 
                               style={{ width: `${sectionStats.compliedPct}%` }}
                             />
                             <div 
                               className="bg-yellow-500" 
                               style={{ width: `${sectionStats.partiallyPct}%` }}
                             />
                             <div 
                               className="bg-red-500" 
                               style={{ width: `${sectionStats.notCmpPct}%` }}
                             />
                              <div 
                                className="bg-gray-400" 
                                style={{ width: `${sectionStats.pendingPct}%` }}
                              />
                           </div>
                         </div>

                          <div className="flex flex-wrap justify-between text-xs text-gray-500 mt-1">
                            <span className="text-green-600">Complied: {sectionStats.complied}</span>
                            <span className="text-yellow-600">Partial: {sectionStats.partially}</span>
                            <span className="text-red-600">Not complied: {sectionStats.notCmp}</span>
                            <span className="text-gray-600">Pending / N&A: {sectionStats.pending}</span>
                          </div>

                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {filteredGrouped.map((sec) => {
          const isCollapsed = sectionCollapsed.has(sec.name);
          const stats = computeStats(sec.items);
          return (
            <div
              key={sec.name}
              className="bg-white rounded-lg shadow border overflow-hidden"
            >
              <div
                className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200 cursor-pointer hover:from-blue-100 hover:to-indigo-100 transition-colors"
                onClick={() => toggleSection(sec.name)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-800">
                      {sec.name}
                    </h3>
                    <span className="text-sm text-gray-500">
                      ({sec.items.length} KPIs)
                    </span>
                  </div>
                  <div className="flex items-center gap-4 flex-wrap justify-end">
                    <div className="text-right">
                      <div className="text-xl font-bold text-green-600">
                        {stats.compliedPct}% <span className="text-sm text-gray-600">({stats.complied})</span>
                      </div>
                      <div className="text-xs text-gray-600">Complied</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-yellow-600">
                        {stats.partiallyPct}% <span className="text-sm text-gray-600">({stats.partially})</span>
                      </div>
                      <div className="text-xs text-gray-600">Partially Complied</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-red-600">
                        {stats.notCmpPct}% <span className="text-sm text-gray-600">({stats.notCmp})</span>
                      </div>
                      <div className="text-xs text-gray-600">Not Complied</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-gray-600">
                        {stats.pendingPct}% <span className="text-sm text-gray-600">({stats.pending})</span>
                      </div>
                      <div className="text-xs text-gray-600">Pending / N&A</div>
                    </div>
                    <div className="flex items-center">
                      {isCollapsed ? (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="flex h-2 rounded-full overflow-hidden">
                      <div 
                        className="bg-green-500" 
                        style={{ width: `${stats.compliedPct}%` }}
                      />
                      <div 
                        className="bg-yellow-500" 
                        style={{ width: `${stats.partiallyPct}%` }}
                      />
                      <div 
                        className="bg-red-500" 
                        style={{ width: `${stats.notCmpPct}%` }}
                      />
                      <div 
                        className="bg-gray-400" 
                        style={{ width: `${stats.pendingPct}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {!isCollapsed && (
                <div className="p-6">
                  <div className="space-y-4">
                    {sec.items.map((it) => (
                      <div
                        key={it.srNo}
                        id={`question-${it.srNo}`}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-medium text-gray-600">
                            {it.srNo}
                          </div>
                          <div className="flex-1">
                            <p className="text-gray-800 font-medium mb-3">
                              {it.question}
                            </p>

                            {isEditMode && assessment?.status !== "submitted" ? (
                              <div className="space-y-3">
                                <div className="flex flex-wrap gap-2">
                                  {STATUS_OPTIONS.map((st) => (
                                    <button
                                      key={st || "na"}
                                      onClick={() =>
                                        onStatusChange(it.srNo, st)
                                      }
                                      className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                                        it.status === st
                                          ? statusPill(st)
                                          : "text-gray-600 bg-gray-100 border-gray-300 hover:bg-gray-200"
                                      }`}
                                    >
                                      {st || "Not Applicable"}
                                    </button>
                                  ))}
                                </div>

                                <div>
                                  {(() => {
                                    const requiresRemarks = it.status === "Partially Complied" || it.status === "Not Complied";
                                    const isMissing = requiresRemarks && !it.remarks?.trim();
                                    return (
                                      <>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Remarks {requiresRemarks && <span className="text-red-500">*</span>}
                                        </label>
                                        <textarea
                                          defaultValue={it.remarks || ""}
                                          onChange={(e) =>
                                            onRemarksChange(it.srNo, e.target.value)
                                          }
                                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent resize-none ${
                                            isMissing
                                              ? 'border-red-400 bg-red-50 focus:ring-red-500 placeholder-red-400'
                                              : 'border-gray-300 focus:ring-blue-500'
                                          }`}
                                          rows={2}
                                          placeholder={requiresRemarks ? "Remarks required — explain non-compliance…" : "Add remarks or observations…"}
                                        />
                                        {isMissing && (
                                          <p className="text-xs text-red-500 mt-1">Remarks are mandatory for "{it.status}" items</p>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-4">
                                <div
                                  className={`flex items-center gap-2 px-3 py-1 rounded-full border ${statusPill(
                                    it.status
                                  )}`}
                                >
                                  {statusIcon(it.status)}
                                  <span className="text-sm font-medium">
                                    {it.status || "Not Applicable"}
                                  </span>
                                </div>

                                {it.remarks ? (
                                  <div className="flex-1">
                                    <p className="text-sm text-gray-600">
                                      <span className="font-medium">
                                        Remarks:
                                      </span>{" "}
                                      {it.remarks}
                                    </p>
                                  </div>
                                ) : null}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    {sec.items.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p className="text-lg font-medium">No KPIs found</p>
                        <p className="text-sm">Try adjusting your search</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {filteredGrouped.length === 0 && (
          <div className="text-center py-16 text-gray-500 bg-white rounded-lg shadow border">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">No items match your search</p>
          </div>
        )}

        {overallStats.pending === 0 && overallStats.total > 0 && (
          <div 
            id="completion-message"
            className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg shadow-sm p-6 text-center"
          >
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-green-800 mb-2">
              Assessment Complete! 🎉
            </h3>
            <p className="text-green-700 mb-4">
              You have answered all {overallStats.total} questions in this assessment.
            </p>
            <div className="flex items-center justify-center gap-4 text-sm text-green-600">
              <span className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                {overallStats.complied} Complied
              </span>
              <span className="flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {overallStats.partially} Partially
              </span>
              <span className="flex items-center gap-1">
                <XCircle className="w-4 h-4" />
                {overallStats.notCmp} Not Complied
              </span>
            </div>
            {isEditMode && assessment?.status !== "submitted" && (
              <div className="mt-4">
                <button
                  onClick={onSubmitAssessment}
                  className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors flex items-center gap-2 mx-auto"
                  disabled={loading}
                >
                  <ShieldCheck className="w-5 h-5" />
                  Submit Assessment
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {lastSaved && (
        <div className="fixed bottom-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg">
          Last saved: {lastSaved.toLocaleTimeString()}
        </div>
      )}
      {saveError && (
        <div className="fixed bottom-4 right-4 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg">
          {saveError}
        </div>
      )}
    </div>
  );
};

export default AssessmentDetail;
